import {toBaseScalarPricingPayload} from "@/features/pricing";
import type {UpdateLiveEventRequest} from "../../../../features/live-events";
import {toPurchaseLimitPayload} from "../../../../components/offering-form/general/settings/purchase-limit-payload";
import {EditLiveEventFormValues} from "../types";

/**
 * Converts form values to v2 domain UpdateLiveEventRequest format.
 */
export function editLiveEventDto(
  values: EditLiveEventFormValues
): UpdateLiveEventRequest {
  const redirectType =
    values.redirectAfterPurchase === "custom-url"
      ? "url"
      : values.redirectAfterPurchase ?? "none";

  // Extract first cover image URL from cover_images array if available
  const coverImageUrl =
    values.cover_images && values.cover_images.length > 0
      ? values.cover_images[0].media_url
      : values.coverImage || null;

  return {
    id: values.id,
    // The Pricing Model, the base-currency scalars and the rows. Under Pay What You Want
    // the base row is split out of `currencies` on the way; Free and Single Fee send what
    // they always have.
    ...toBaseScalarPricingPayload(values),
    description: values.description,
    hideFromMenu: values.hideFromProfile,
    name: values.name,
    attendees: values.attendees ? values.attendees : -1,
    customLink: values.callLocation === "custom_url" ? values.customUrl : null,
    hasCustomLink: !!values.customUrl && values.callLocation === "custom_url",
    hasGoogleMeet: values.callLocation === "google_meet",
    useZoom: values.callLocation === "zoom",
    hasPhysicalLocation: values.callLocation === "physical_location",
    locationAddress:
      values.callLocation === "physical_location" ? values.locationAddress : null,
    locationLat: values.callLocation === "physical_location" ? values.locationLat : null,
    locationLng: values.callLocation === "physical_location" ? values.locationLng : null,
    isLocationPublic:
      values.callLocation === "physical_location"
        ? values.isLocationPublic !== false
        : true,
    isPaused: values.closeRegistration,
    enableCurrencyConversion: values.callPriceType !== "free",
    selectedCurrencies:
      values.callPriceType === "free" ? [] : values.selected_currencies || [],
    sessionInfo:
      values.topicType === "series"
        ? values.classSeries.map((item) => ({
            name: item.name,
            startDate: item.startDate,
            startTime: item.startTime,
            duration:
              item.duration === "-1" && item.customDuration
                ? item.customDuration
                : Number(item.duration),
          }))
        : [
            {
              name: values.name,
              startDate: values.startDate as string,
              startTime: values.startTime as string,
              duration:
                values.duration === "-1" && values.customDuration
                  ? values.customDuration
                  : Number(values.duration),
            },
          ],
    topicType: values.topicType,
    requestPhoneNumber: values.requestCustomerPhoneNumber,
    ...toPurchaseLimitPayload(values),
    coverImage: coverImageUrl || "",
    urlRedirect: redirectType === "url" ? values.url_redirect : null,
    redirectType,
    telegramGroupChatId:
      redirectType === "telegram" ? values.telegramGroupId ?? null : null,
    bookingQuestions:
      values.bookingQuestions.length > 0
        ? values.bookingQuestions.map((q) => {
            if (
              q.answerType === "checkbox" ||
              q.answerType === "dropdown" ||
              q.answerType === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answerType as "checkbox" | "dropdown" | "radio-button",
                options: q.options.map((o) => o.optionLabel),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answerType as "short-text" | "email" | "radio",
              };
            }
          })
        : [],
  };
}
